import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "../../app/constants";
import Container from "../../components/Container";
import Footer from "../../components/footer";
import FullPageLoader from "../../components/FullPageLoader";
import Header from "../../components/Header";
import { AuthContext } from "../../context/authContext";
import { COLORS } from "../../constants/colors";
import { fetchFoodOrders } from "./service";

const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const OrderRow = ({ label, value }) => (
  <Flex justify="space-between" gap="3" fontSize="xs">
    <Text color="#707070">{label}</Text>
    <Text fontWeight="600" textAlign="right">
      {value || "N/A"}
    </Text>
  </Flex>
);

const getStatusColor = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "paid" || normalizedStatus === "delivered") return "green";
  if (normalizedStatus === "cancelled" || normalizedStatus === "failed") return "red";
  return "orange";
};

const FoodOrders = () => {
  const pageBg = useColorModeValue(COLORS.bgGrey, COLORS.darkModeBg);
  const cardBg = useColorModeValue(COLORS.white, COLORS.grey);
  const heroBg = useColorModeValue(COLORS.primary, COLORS.black);
  const appTextColor = useColorModeValue(COLORS.primary, COLORS.lightGrey);
  const vendorBg = useColorModeValue("#f9f9f9", "#1f1f2e");
  const mutedColor = useColorModeValue("#707070", COLORS.lightGrey);
  const {
    userInfo: { userId },
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setOrders([]);
      return;
    }

    setError("");
    fetchFoodOrders(userId, setLoading, setOrders, setError);
  }, [userId]);

  if (loading) return <FullPageLoader bg={pageBg} />;

  return (
    <Stack pb="15vh" bg={pageBg} minH="100vh">
      <Box shadow="sm">
        <Container>
          <Header menu />
        </Container>
      </Box>

      <Container>
        <Stack spacing="5" pt="5" pb="10">
          <Box bg={heroBg} borderRadius="16px" p="5" color={COLORS.white}>
            <Flex align="center" gap="2">
              <Button
                size="sm"
                bg="rgba(255,255,255,0.14)"
                color={COLORS.white}
                onClick={() => navigate(PROTECTED_PATHS.FOOD_LISTINGS)}
                _hover={{ bg: "rgba(255,255,255,0.22)" }}
                minW="8"
                px="2"
              >
                <BsArrowLeft />
              </Button>
              <Text fontSize="xl" fontWeight="700">
                Food Orders
              </Text>
            </Flex>
            <Text mt="1" fontSize="sm" opacity="0.9">
              View food orders you have made.
            </Text>
            <Text mt="3" fontSize="xs" opacity="0.8">
              {orders.length} order{orders.length === 1 ? "" : "s"} found
            </Text>
          </Box>

          {error ? (
            <Flex bg={cardBg} borderRadius="12px" p="4" justify="center">
              <Text textAlign="center" color={appTextColor}>
                {error}
              </Text>
            </Flex>
          ) : null}

          {!error && orders.length === 0 ? (
            <Flex bg={cardBg} borderRadius="12px" p="6" justify="center">
              <Text textAlign="center" color={appTextColor}>
                You have not made any food orders yet.
              </Text>
            </Flex>
          ) : null}

          <SimpleGrid columns={[1, 1, 2]} spacing="4">
            {orders.map((order) => (
              <Stack
                key={order.id}
                bg={cardBg}
                borderRadius="10px"
                overflow="hidden"
                spacing="0"
                shadow="sm"
              >
                {order.image ? (
                  <Image
                    src={order.image}
                    alt={order.productName}
                    w="100%"
                    h="132px"
                    objectFit="cover"
                  />
                ) : (
                  <Flex
                    w="100%"
                    h="132px"
                    align="center"
                    justify="center"
                    bg={`linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.lightGrey} 100%)`}
                    color={COLORS.white}
                    fontWeight="600"
                    textAlign="center"
                    p="4"
                  >
                    No image available
                  </Flex>
                )}

                <Stack p="3" spacing="2">
                  <Flex justify="space-between" align="center" gap="2">
                    <Text fontWeight="700" color={appTextColor} noOfLines={1} flex="1">
                      {order.productName}
                    </Text>
                    <Badge
                      colorScheme={getStatusColor(order.status)}
                      borderRadius="999px"
                      px="2"
                      py="1"
                      whiteSpace="nowrap"
                    >
                      {order.status}
                    </Badge>
                  </Flex>

                  {/* <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                    {order.description || "No description provided."}
                  </Text> */}

                  <Flex justify="space-between" gap="2" fontSize="xs" color={appTextColor}>
                    <Text fontWeight="700" noOfLines={1}>
                      {order.amount}
                    </Text>
                    <Text color={mutedColor} noOfLines={1} textAlign="right">
                      Qty: {order.quantity}
                    </Text>
                  </Flex>

                  <Flex
                    align="center"
                    gap="2"
                    px="2"
                    py="1"
                    bg={vendorBg}
                    borderRadius="6px"
                    cursor="pointer"
                    _hover={{ opacity: 0.85 }}
                    onClick={() => navigate(`/vendor-foods/${encodeURIComponent(order.vendorName)}`)}
                  >
                    <Avatar
                      size="xs"
                      name={order.vendorName}
                      bg={COLORS.primary}
                      color={COLORS.white}
                    />
                    <Text fontSize="xs" fontWeight="600" color={appTextColor} noOfLines={1}>
                      {order.vendorName}
                    </Text>
                  </Flex>

                  <Stack spacing="2" color={appTextColor}>
                    <OrderRow label="Date" value={formatDate(order.createdAt)} />
                  </Stack>
                </Stack>
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>

      <Footer />
    </Stack>
  );
};

export default FoodOrders;
